import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSave, FiPlus, FiTrash2, FiX, FiCheck, FiChevronUp, FiChevronDown, FiEye } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../../api/api';
import VariantImageManager from '../../components/VariantImageManager';

// Add MultiSelect component
const MultiSelect = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleOption = (tagId) => {
    const newValue = value.includes(tagId)
      ? value.filter(id => id !== tagId)
      : [...value, tagId];
    onChange(newValue);
  };

  return (
    <div className="relative">
      <div
        className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-md shadow-sm cursor-pointer bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-2">
          {value.length > 0 ? (
            options
              .filter(option => value.includes(option.id))
              .map(option => (
                <span
                  key={option.id}
                  className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-indigo-100 text-indigo-800"
                >
                  {option.name}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleOption(option.id);
                    }}
                    className="ml-1 hover:text-indigo-500"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                </span>
              ))
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="p-2">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <ul className="max-h-60 overflow-auto py-1">
            {filteredOptions.map(option => (
              <li
                key={option.id}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleOption(option.id);
                }}
              >
                <span>{option.name}</span>
                {value.includes(option.id) && (
                  <FiCheck className="h-5 w-5 text-indigo-600" />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false); // Add this state
  
  // Add state for categories, brands, and tags
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [tags, setTags] = useState([]);

  // Add newVariant state
  const [newVariant, setNewVariant] = useState({
    size: '',
    color: '',
    color_code: '#000000',
    stock: 0,
    sku: '',
    images: []
  });

  const [productData, setProductData] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    is_active: true,
    category: { id: '', name: '' },
    brand: { id: '', name: '' },
    tags: [],
    variants: [], // Initialize with empty array
    highlights: [],
    specifications: [],
    styling_tips: [], // Initialize with empty array
    active_offers: [],
    production_time: '',
    shipping: '',
    returns_policy: '',
    is_new_arrival: false,
    is_featured: false
  });

  // Add these states after other state declarations
  const [additionalInfo, setAdditionalInfo] = useState({
    currentHighlight: '',
    currentSpecification: '',
    currentStylingTip: ''
  });

  // Add generateSKU function
  const generateSKU = (size, color) => {
    const prefix = 'DYX';
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const sizeCode = size.replace(/\s+/g, '').substring(0, 2).toUpperCase();
    const colorCode = color.replace(/\s+/g, '').substring(0, 2).toUpperCase();
    return `${prefix}${sizeCode}${colorCode}${randomNum}`;
  };

  // Fetch categories, brands, and tags
  useEffect(() => {
    const fetchTaxonomyData = async () => {
      try {
        const [categoriesData, brandsData, tagsData] = await Promise.all([
          api.getCategories(),
          api.getBrands(),
          api.getTags()
        ]);

        // Filter active categories
        const activeCategories = Array.isArray(categoriesData) 
          ? categoriesData.filter(cat => cat.is_active)
          : (categoriesData?.results || []).filter(cat => cat.is_active);

        // Filter active brands
        const activeBrands = Array.isArray(brandsData)
          ? brandsData.filter(brand => brand.is_active)
          : (brandsData?.results || []).filter(brand => brand.is_active);

        setCategories(activeCategories);
        setBrands(activeBrands);
        setTags(tagsData);
      } catch (error) {
        console.error('Error fetching taxonomy data:', error);
        toast.error('Failed to load categories, brands, or tags');
      }
    };

    fetchTaxonomyData();
  }, []);

  // Fetch product data
  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.getProduct(id);
      if (!response) throw new Error('Product not found');
      
      // Ensure these fields are properly set with default values if they're null/undefined
      setProductData({
        ...response,
        production_time: response.production_time || '',
        shipping: response.shipping || '',
        returns_policy: response.returns_policy || ''
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to fetch product');
      navigate('/admin/products/list');
    } finally {
      setLoading(false);
    }
  };

  const handleBasicInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTagsChange = (newTagIds) => {
    const selectedTags = tags.filter(tag => newTagIds.includes(tag.id));
    setProductData(prev => ({
      ...prev,
      tags: selectedTags
    }));
  };

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...productData.variants];
    updatedVariants[index] = {
      ...updatedVariants[index],
      [field]: value
    };
    setProductData(prev => ({ ...prev, variants: updatedVariants }));
  };

  const handleAddVariant = async () => {
    // Validate required fields
    if (!newVariant.size || !newVariant.color || !newVariant.sku) {
      toast.error('Please fill in all required fields (Size, Color, and SKU)');
      return;
    }

    // Validate stock is a positive number
    if (!newVariant.stock || newVariant.stock < 0) {
      toast.error('Stock must be a positive number');
      return;
    }

    // Show loading state
    const loadingToast = toast.loading('Adding new variant...');
    
    try {
      // Call the API to add the variant
      const response = await api.addProductVariant(id, {
        size: newVariant.size,
        color: newVariant.color,
        color_code: newVariant.color_code,
        stock: parseInt(newVariant.stock),
        sku: newVariant.sku
      });

      // Update the local state with the new variant
      setProductData(prev => ({
        ...prev,
        variants: [...prev.variants, response]
      }));

      // Reset the form
      setNewVariant({
        size: '',
        color: '',
        color_code: '#000000',
        stock: 0,
        sku: '',
        images: []
      });

      // Show success message
      toast.dismiss(loadingToast);
      toast.success('Variant added successfully');

    } catch (error) {
      // Handle errors
      toast.dismiss(loadingToast);
      toast.error(error.message || 'Failed to add variant');
      console.error('Error adding variant:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.updateProduct(id, {
        ...productData,
        production_time: productData.production_time,
        shipping: productData.shipping,
        returns_policy: productData.returns_policy,
        category_id: productData.category.id,
        brand_id: productData.brand.id,
        tag_ids: productData.tags.map(tag => tag.id)
      });

      // Update variants
      for (const variant of productData.variants) {
        if (variant.id) {
          await api.updateVariant(id, variant.id, variant);
        } else {
          await api.addVariant(id, variant);
        }
      }

      // Update other sections
      await Promise.all([
        ...productData.highlights.map(h => api.updateHighlight(id, h.id, h)),
        ...productData.specifications.map(s => api.updateSpecification(id, s.id, s)),
        ...productData.styling_tips.map(t => api.updateStylingTip(id, t.id, t))
      ]);

      toast.success('Product updated successfully');
      navigate('/admin/products/list');
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(error.message || 'Failed to update product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveBasicInfo = async () => {
    try {
      await api.updateProduct(id, {
        name: productData.name,
        category_id: productData.category.id,
        brand_id: productData.brand.id,
        tag_ids: productData.tags.map(tag => tag.id),
        price: productData.price,
        sale_price: productData.sale_price,
        description: productData.description,
        production_time: productData.production_time,
        shipping: productData.shipping,
        returns_policy: productData.returns_policy,
        is_new_arrival: productData.is_new_arrival,
        is_featured: productData.is_featured
      });
      
      toast.success('Basic information updated successfully');
    } catch (error) {
      toast.error('Failed to update basic information');
      console.error('Error updating basic info:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  const VariantItem = ({ variant, index }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedVariant, setEditedVariant] = useState({
      ...variant,
      color_code: variant.color_code || '#000000',
      is_active: variant.is_active ?? true // Default to true if not set
    });

    // Add toggle active status handler
    const handleToggleActive = async () => {
      try {
        const updatedVariant = await api.updateVariant(id, variant.id, {
          ...variant,
          is_active: !variant.is_active
        });

        const updatedVariants = [...productData.variants];
        updatedVariants[index] = updatedVariant;
        setProductData(prev => ({ ...prev, variants: updatedVariants }));
        
        toast.success(`Variant ${updatedVariant.is_active ? 'activated' : 'deactivated'} successfully`);
      } catch (error) {
        toast.error('Failed to update variant status');
        console.error('Error updating variant status:', error);
      }
    };

    const handleSaveVariant = async () => {
      if (isSubmitting) return;
      
      setIsSubmitting(true);
      try {
        // Validate required fields
        if (!editedVariant.size || !editedVariant.color || !editedVariant.sku) {
          throw new Error('Size, color, and SKU are required');
        }

        // Ensure stock is a valid number
        if (isNaN(editedVariant.stock) || editedVariant.stock < 0) {
          throw new Error('Stock must be a valid number');
        }

        const updatedVariant = await api.updateVariant(id, variant.id, {
          size: editedVariant.size,
          color: editedVariant.color,
          color_code: editedVariant.color_code,
          stock: parseInt(editedVariant.stock),
          sku: editedVariant.sku
        });

        // Update the local state with the response from the server
        const updatedVariants = [...productData.variants];
        updatedVariants[index] = updatedVariant;
        setProductData(prev => ({ ...prev, variants: updatedVariants }));
        
        setIsEditing(false);
        toast.success('Variant updated successfully');
      } catch (error) {
        toast.error(error.message || 'Failed to update variant');
        console.error('Error updating variant:', error);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className={`border rounded-lg p-4 mb-4 ${!variant.is_active ? 'bg-gray-50' : ''}`}>
        {isEditing ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Size Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Size <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editedVariant.size}
                  onChange={(e) => setEditedVariant(prev => ({ ...prev, size: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Size"
                  disabled={isSubmitting}
                />
              </div>

              {/* Color Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editedVariant.color}
                  onChange={(e) => setEditedVariant(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Color Name"
                  disabled={isSubmitting}
                />
              </div>

              {/* Color Code Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color Code <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editedVariant.color_code}
                    onChange={(e) => setEditedVariant(prev => ({ ...prev, color_code: e.target.value }))}
                    className="w-14 h-9 p-0 border border-gray-300 rounded-md cursor-pointer"
                    disabled={isSubmitting}
                  />
                  <input
                    type="text"
                    value={editedVariant.color_code}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(newValue)) {
                        setEditedVariant(prev => ({ ...prev, color_code: newValue }));
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="#000000"
                    maxLength={7}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Stock Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stock <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={editedVariant.stock}
                  onChange={(e) => setEditedVariant(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Stock"
                  min="0"
                  disabled={isSubmitting}
                />
              </div>

              {/* SKU Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editedVariant.sku}
                  onChange={(e) => setEditedVariant(prev => ({ ...prev, sku: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="SKU"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editedVariant.is_active}
                    onChange={(e) => setEditedVariant(prev => ({ 
                      ...prev, 
                      is_active: e.target.checked 
                    }))}
                    className="form-checkbox h-4 w-4 text-indigo-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveVariant}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between items-start">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                <div>
                  <p className="text-sm font-medium text-gray-500">Size</p>
                  <p className="mt-1 text-sm text-gray-900">{variant.size}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Color</p>
                  <div className="flex items-center mt-1">
                    <div
                      className="w-5 h-5 rounded-full border shadow-sm mr-2"
                      style={{ 
                        backgroundColor: variant.color_code,
                        borderColor: variant.color_code?.toLowerCase() === '#ffffff' ? '#e5e7eb' : variant.color_code 
                      }}
                    />
                    <p className="text-sm text-gray-900">{variant.color}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Color Code</p>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{variant.color_code}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Stock</p>
                  <p className="mt-1 text-sm text-gray-900">{variant.stock}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">SKU</p>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{variant.sku}</p>
                </div>
              </div>
              <div className="flex items-center justify-between col-span-full">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Add this handler function
  const handleAdditionalInfoSubmit = async (type) => {
    if (!additionalInfo[`current${type}`].trim()) {
      toast.error(`Please enter a ${type.toLowerCase()}`);
      return;
    }

    const loadingToast = toast.loading(`Adding ${type.toLowerCase()}...`);
    try {
      let response;
      
      switch (type) {
        case 'Highlight':
          response = await api.createProductHighlight(id, {
            text: additionalInfo[`current${type}`]
          });
          // Update highlights state
          setProductData(prev => ({
            ...prev,
            highlights: [
              ...prev.highlights,
              {
                id: response.id,
                text: additionalInfo[`current${type}`],  // Use 'text' instead of 'highlight'
                is_active: true
              }
            ]
          }));
          break;

        case 'Specification':
          response = await api.createProductSpecification(id, {
            specification: additionalInfo[`current${type}`]
          });
          // Update specifications state
          setProductData(prev => ({
            ...prev,
            specifications: [
              ...prev.specifications,
              {
                id: response.id,
                specification: additionalInfo[`current${type}`]
              }
            ]
          }));
          break;

        case 'StylingTip':
          response = await api.createProductStylingTips(id, {
            styling_tips: additionalInfo[`current${type}`]
          });
          // Update styling tips state
          setProductData(prev => ({
            ...prev,
            styling_tips: [
              ...prev.styling_tips,
              {
                id: response.id,
                styling_tips: additionalInfo[`current${type}`]
              }
            ]
          }));
          break;
      }

      // Clear the input field
      setAdditionalInfo(prev => ({
        ...prev,
        [`current${type}`]: ''
      }));

      toast.dismiss(loadingToast);
      toast.success(`${type} added successfully`);
    } catch (error) {
      console.error(`Error adding ${type.toLowerCase()}:`, error);
      toast.error(`Failed to add ${type.toLowerCase()}`);
      toast.dismiss(loadingToast);
    }
  };

  // Add this handler for deletion
  const handleDeleteItem = async (type, index, itemId) => {
    try {
      const loadingToast = toast.loading(`Deleting ${type.toLowerCase()}...`);
      
      switch (type) {
        case 'Highlight':
          await api.deleteProductHighlight(id, itemId);
          setProductData(prev => ({
            ...prev,
            highlights: prev.highlights.filter(item => item.id !== itemId)
          }));
          break;

        case 'Specification':
          await api.deleteProductSpecification(id, itemId);
          setProductData(prev => ({
            ...prev,
            specifications: prev.specifications.filter(item => item.id !== itemId)
          }));
          break;

        case 'StylingTip':
          await api.deleteProductStylingTip(id, itemId);
          setProductData(prev => ({
            ...prev,
            styling_tips: prev.styling_tips.filter(item => item.id !== itemId)
          }));
          break;
      }

      toast.dismiss(loadingToast);
      toast.success(`${type} deleted successfully`);
    } catch (error) {
      console.error(`Error deleting ${type.toLowerCase()}:`, error);
      toast.error(`Failed to delete ${type.toLowerCase()}`);
    }
  };

  // Add this section in your render method, after the variants section
  const renderAdditionalInfo = () => (
    <section className="mt-8">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h2 className="text-xl font-semibold mb-6">Additional Information</h2>
        
        {/* Highlights Section */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Product Highlights</h3>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={additionalInfo.currentHighlight}
                onChange={(e) => setAdditionalInfo(prev => ({ ...prev, currentHighlight: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter product highlight"
              />
              <button
                type="button"
                onClick={() => handleAdditionalInfoSubmit('Highlight')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Add
              </button>
            </div>
            <div className="mt-2">
              {productData.highlights.map((highlight, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                    <span className="text-gray-700">{highlight.text}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteItem('Highlight', index, highlight.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Specifications Section */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Specifications</h3>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={additionalInfo.currentSpecification}
                onChange={(e) => setAdditionalInfo(prev => ({ ...prev, currentSpecification: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter specification"
              />
              <button
                type="button"
                onClick={() => handleAdditionalInfoSubmit('Specification')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Add
              </button>
            </div>
            <div className="mt-2">
              {productData.specifications.map((spec, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                    <span className="text-gray-700">{spec.specification}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteItem('Specification', index, spec.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Styling Tips Section */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Styling Tips</h3>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={additionalInfo.currentStylingTip}
                onChange={(e) => setAdditionalInfo(prev => ({ ...prev, currentStylingTip: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter styling tip"
              />
              <button
                type="button"
                onClick={() => handleAdditionalInfoSubmit('StylingTip')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Add
              </button>
            </div>
            <div className="mt-2">
              {productData.styling_tips.map((tip, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                    <span className="text-gray-700">{tip.styling_tips}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteItem('StylingTip', index, tip.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Edit Product</h1>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
          <div className="grid grid-cols-1 gap-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={productData.name}
                onChange={handleBasicInfoChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                placeholder="Enter product name"
                required
              />
            </div>

            {/* Category and Brand */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={productData.category.id}
                  onChange={handleBasicInfoChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand <span className="text-red-500">*</span>
                </label>
                <select
                  name="brand"
                  value={productData.brand.id}
                  onChange={handleBasicInfoChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  required
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <MultiSelect
                options={tags}
                value={productData.tags.map(tag => tag.id)}
                onChange={handleTagsChange}
                placeholder="Select tags..."
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regular Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    name="price"
                    value={productData.price}
                    onChange={handleBasicInfoChange}
                    className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sale Price
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    name="sale_price"
                    value={productData.sale_price}
                    onChange={handleBasicInfoChange}
                    className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={productData.description}
                onChange={handleBasicInfoChange}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors resize-y"
                placeholder="Enter product description"
              />
            </div>

            {/* Production and Shipping */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Production Time
                </label>
                <input
                  type="text"
                  name="production_time"
                  value={productData.production_time}
                  onChange={handleBasicInfoChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  placeholder="e.g., 4-5 business days"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shipping Information
                </label>
                <input
                  type="text"
                  name="shipping"
                  value={productData.shipping}
                  onChange={handleBasicInfoChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                  placeholder="e.g., Free shipping across India"
                />
              </div>
            </div>

            {/* Returns Policy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Returns Policy
              </label>
              <textarea
                name="returns_policy"
                value={productData.returns_policy}
                onChange={handleBasicInfoChange}
                rows={2}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors resize-y"
                placeholder="Enter returns policy details"
              />
            </div>

            {/* Product Status */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700">Product Status</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_new_arrival"
                    checked={productData.is_new_arrival}
                    onChange={handleBasicInfoChange}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 transition-colors cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Mark as New Arrival</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={productData.is_featured}
                    onChange={handleBasicInfoChange}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 transition-colors cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Mark as Featured</span>
                </label>
              </div>
            </div>
          </div>
          
          {/* Add Save Basic Info Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSaveBasicInfo}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              <FiSave className="w-4 h-4 mr-2" />
              Save Basic Info
            </button>
          </div>
        </section>

        {/* Variants Section */}
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Variants</h2>
          </div>
          
          {productData?.variants
            ?.filter(variant => variant?.is_active)
            .map((variant, index) => (
              <VariantItem
                key={variant?.id || index}
                variant={variant}
                index={index}
              />
            ))}
        </section>

        {/* Variant Images Section */}
        <section className="mt-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-semibold mb-6">Variant Images</h2>
            
            {productData.variants
              .filter(variant => variant.is_active)
              .map((variant) => (
                <div key={variant.id} className="mb-8">
                  <VariantImageManager
                    productId={id}
                    variant={variant}
                    onImagesChange={(updatedImages) => {
                      // Update local state if needed
                      setProductData(prev => ({
                        ...prev,
                        variants: prev.variants.map(v => 
                          v.id === variant.id 
                            ? { ...v, images: updatedImages }
                            : v
                        )
                      }));
                    }}
                  />
                </div>
              ))}
          </div>
        </section>

        {/* Add New Variant Form */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Variant</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Size Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Size <span className="text-red-500">*</span>
                </label>
                <select
                  value={newVariant.size}
                  onChange={(e) => {
                    const newSize = e.target.value;
                    const newSku = newSize && newVariant.color 
                      ? generateSKU(newSize, newVariant.color)
                      : newVariant.sku;
                    setNewVariant({ ...newVariant, size: newSize, sku: newSku });
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Size</option>
                  <option value="Free Size">Free Size</option>
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="XXL">XXL</option>
                </select>
              </div>

              {/* Color Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newVariant.color}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    const newSku = newColor && newVariant.size 
                      ? generateSKU(newVariant.size, newColor)
                      : newVariant.sku;
                    setNewVariant({ ...newVariant, color: newColor, sku: newSku });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Royal Blue"
                />
              </div>

              {/* Color Code Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color Code <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={newVariant.color_code}
                    onChange={(e) => setNewVariant({ ...newVariant, color_code: e.target.value })}
                    className="w-14 h-9 p-0 border border-gray-300 rounded-md cursor-pointer"
                  />
                  <input
                    type="text"
                    value={newVariant.color_code}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(newValue)) {
                        setNewVariant({ ...newVariant, color_code: newValue });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                    placeholder="#000000"
                    maxLength={7}
                  />
                </div>
              </div>

              {/* Stock Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stock <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={newVariant.stock}
                    onChange={(e) => setNewVariant({ ...newVariant, stock: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter stock quantity"
                    min="0"
                  />
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => setNewVariant(prev => ({ ...prev, stock: parseInt(prev.stock || 0) + 1 }))}
                      className="px-2 py-1 border border-gray-300 rounded-t-md hover:bg-gray-50"
                    >
                      <FiChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewVariant(prev => ({ ...prev, stock: Math.max(0, parseInt(prev.stock || 0) - 1) }))}
                      className="px-2 py-1 border border-gray-300 rounded-b-md hover:bg-gray-50"
                    >
                      <FiChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* SKU Input */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newVariant.sku}
                    onChange={(e) => setNewVariant({ ...newVariant, sku: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                    placeholder="Enter SKU or click generate"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newVariant.size && newVariant.color) {
                        const newSku = generateSKU(newVariant.size, newVariant.color);
                        setNewVariant({ ...newVariant, sku: newSku });
                      } else {
                        toast.error('Both size and color are required to generate SKU');
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>

            {/* Preview and Add Button */}
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-lg border shadow-sm"
                  style={{ 
                    backgroundColor: newVariant.color_code,
                    borderColor: newVariant.color_code?.toLowerCase() === '#ffffff' ? '#e5e7eb' : newVariant.color_code 
                  }}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {newVariant.size} - {newVariant.color}
                  </p>
                  <p className="text-sm text-gray-500">
                    Stock: {newVariant.stock} | SKU: {newVariant.sku}
                  </p>
                </div>
              </div>
              
              <button
                type="button"
                onClick={handleAddVariant}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FiPlus className="mr-2 -ml-1 h-5 w-5" />
                Add Variant
              </button>
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        {renderAdditionalInfo()}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProduct;













































